import React from 'react';
import CellValue from '@app/shared/components/cell-value/CellValue';
import { shortDateViewRules } from '@app/shared/components/cell-value/CellValueConfigurations';
import { GridSelectField } from '@app/shared/components/grid-controls/grid-select-field/GridSelectField';
import { Alignment } from '@app/shared/enums/alignment.enum';
import { SponsorIrrAndHurdleTab } from '@app/shared/models/contracts/enums/shared-enums';
import classNames from 'classnames';
import { FormProvider, UseFormReturn } from 'react-hook-form';
import { InstrumentOption } from './hooks/useInstrumentOptions';
import styles from '../sponsor-irr-and-hurdle.module.scss';
import { l } from '@locale/setupI18n';
import { isYearGreyedOutInFocusMode } from './utils/irrHurdleCalcHelper';

export interface FormData {
  selectedInstrument: string;
}

export interface IRRHurdleTableHeaderProps {
  formMethods: UseFormReturn<FormData>;
  instrumentOptions: InstrumentOption[];
  yearEntries: Record<string, number>;
  navigatedSelectedYear: Optional<string>;
  focusModeType: Optional<keyof typeof SponsorIrrAndHurdleTab>;
  isOpmOnly: boolean;
  parentClassName: string;
}

const IRRHurdleTableHeader: React.FC<IRRHurdleTableHeaderProps> = ({
  formMethods,
  instrumentOptions,
  yearEntries,
  navigatedSelectedYear,
  focusModeType,
  isOpmOnly,
  parentClassName,
}) => {
  const renderYearHeaders = () => {
    const ths: React.ReactNode[] = [];
    Object.entries(yearEntries).forEach(([year, count], idx) => {
      const titleCellProperties =
        year === l('_Historic')
          ? { value: year, alignment: Alignment.Center }
          : { value: year, ...shortDateViewRules, alignment: Alignment.Center };

      ths.push(
        <th
          key={year}
          colSpan={count}
          className={classNames(
            'table-primary__cell--header-tertiary',
            styles[`${parentClassName}__cell--header-tertiary`],
            { [styles['greyed-out']]: isYearGreyedOutInFocusMode(year, navigatedSelectedYear) }
          )}>
          <CellValue {...titleCellProperties} />
        </th>
      );
      if (idx < Object.keys(yearEntries).length - 1) {
        ths.push(
          <th key={`separator-${idx}`} className={styles[`${parentClassName}__col--separator`]} />
        );
      }
    });
    return ths;
  };

  return (
    <thead
      className={`table-primary__sticky-section ${
        isOpmOnly
          ? `table-primary__sticky-section--opm--sponsor-irr-and-hurdle-header-${
              focusModeType ? 'focused' : 'unfocused'
            }`
          : `table-primary__sticky-section--sponsor-irr-and-hurdle-header-${
              focusModeType ? 'focused' : 'unfocused'
            }`
      }`}>
      <tr>
        <th className={styles[`${parentClassName}__cell--options-select`]}>
          <FormProvider {...formMethods}>
            <GridSelectField
              name="selectedInstrument"
              options={instrumentOptions}
              disabled={Boolean(focusModeType)}
            />
          </FormProvider>
        </th>
        {renderYearHeaders()}
      </tr>
    </thead>
  );
};

export default IRRHurdleTableHeader;
